import { Box, Stack, SxProps, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { BaseTheme } from 'design-system';
import type { ReactNode } from 'react';
import useIsViewport from 'hooks/useIsViewport';

export interface ToggleOption {
  value: string;
  label?: string;
  icon?: ReactNode;
}

type MultiToggleVariant = 'outlined' | 'inset';

const outlinedToggleButtonStyle = (theme: BaseTheme, color: 'primary' | 'secondary'): SxProps<BaseTheme> => ({
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[300]}`,
  paddingY: theme.spacing(0.5),
  backgroundColor: theme.palette.common.white,
  color: color === 'secondary' ? theme.palette.secondary.main : theme.palette.primary.light,
  '&.Mui-selected': {
    backgroundColor: color === 'secondary' ? theme.palette.secondary.main : theme.palette.primary.light,
    color: theme.palette.common.white,
  },
  '&.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton': {
    border: `1px solid ${theme.palette.grey[300]}`,
  },
});

const insetToggleButtonStyle = (theme: BaseTheme): SxProps<BaseTheme> => ({
  m: 0.5,
  border: 'none',
  borderRadius: theme.spacing(1),
  transition: theme.transitions.create(['background-color', 'box-shadow', 'color'], { duration: 200 }),
  '&.MuiToggleButtonGroup-firstButton, &.MuiToggleButtonGroup-middleButton, &.MuiToggleButtonGroup-lastButton': {
    borderRadius: theme.spacing(1),
    border: 'none',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
    pointerEvents: 'none',
    cursor: 'default',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
});

const insetGroupStyle = (theme: BaseTheme) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.tertiary.main + '50',
});

const MultiToggle = ({
  defaultValue,
  toggleOptions,
  hideTitle = false,
  color = 'secondary',
  size = 'small',
  fullWidth = true,
  variant = 'outlined',
  sxProps = {},
  onToggle,
}: {
  toggleOptions: ToggleOption[];
  onToggle: (selection: string) => void;
  hideTitle?: boolean;
  color?: 'primary' | 'secondary';
  size?: 'small' | 'medium' | 'large';
  defaultValue: string;
  variant?: MultiToggleVariant;
  sxProps?: SxProps<BaseTheme>;
  fullWidth?: boolean;
}) => {
  const isMobile = useIsViewport('sm');
  const isInset = variant === 'inset';

  return (
    <ToggleButtonGroup
      exclusive
      value={defaultValue}
      onChange={(_, val) => {
        if (val !== null) onToggle(val);
      }}
      size={isInset ? 'medium' : size}
      fullWidth={isInset ? true : isMobile && fullWidth}
      sx={isInset ? insetGroupStyle : undefined}
    >
      {toggleOptions.map((toggleOption) => (
        <ToggleButton
          sx={(theme) => ({
            ...(isInset ? insetToggleButtonStyle(theme) : outlinedToggleButtonStyle(theme, color)),
            ...(!isInset ? sxProps : {}),
          })}
          value={toggleOption.value}
          key={toggleOption.value}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: isInset ? 1 : undefined,
              fontSize: isInset ? undefined : '0.75rem',
              whiteSpace: isInset ? 'nowrap' : undefined,
            }}
          >
            {toggleOption.icon && (
              <Box component="span" sx={{ marginRight: isInset || hideTitle ? 0 : 0.5 }}>
                {toggleOption.icon}
              </Box>
            )}
            {(!hideTitle || isInset) && (
              <Typography
                variant={isInset ? 'body2' : undefined}
                sx={(theme) => ({
                  fontSize: isInset ? '0.875rem' : undefined,
                  fontWeight: theme.typography.fontWeightMedium,
                })}
              >
                {toggleOption.label ?? toggleOption.value}
              </Typography>
            )}
          </Stack>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default MultiToggle;
